import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { isAuthenticated, getToken } from '@/utils/auth'

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue'),
    meta: {
      title: '登录 - 四零八研学社管理系统'
    }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/views/ForgotPassword.vue'),
    meta: {
      title: '忘记密码 - 四零八研学社管理系统'
    }
  },
  {
    path: '/',
    component: () => import('@/layout/MainLayout.vue'),
    redirect: '/dashboard',
    children: [
      // Dashboard
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/views/Dashboard.vue'),
        meta: { title: '首页' }
      },
      // User Profile
      {
        path: 'user/profile',
        name: 'UserProfile',
        component: () => import('@/views/UserProfile.vue'),
        meta: { title: '个人信息' }
      },
      // Employee Management
      {
        path: 'employee/list',
        name: 'EmployeeList',
        component: () => import('@/views/EmployeeManagement/EmployeeList.vue'),
        meta: { title: '员工管理' }
      },
      // Trading Center
      {
        path: 'trading-center',
        name: 'TradingCenter',
        component: () => import('@/views/TradingCenter.vue'),
        meta: { title: '添加商品分类' }
      },
      {
        path: 'trading-center/awaiting-approval',
        name: 'AwaitingApproval',
        component: () => import('@/views/AwaitingApproval.vue'),
        meta: { title: '审核商品分类' }
      },
      {
        path: 'trading-center/all-categories',
        name: 'AllCategories',
        component: () => import('@/views/AllCategories.vue'),
        meta: { title: '商品分类列表' }
      },
      // Customer Service Management
      {
        path: 'customer-service/work-order-assignment',
        name: 'WorkOrderAssignment',
        component: () => import('@/views/WorkOrderAssignment.vue'),
        meta: { title: '工单分配' }
      },
      {
        path: 'customer-service/work-order-processing',
        name: 'WorkOrderProcessing',
        component: () => import('@/views/WorkOrderProcessing.vue'),
        meta: { title: '处理工单' }
      },
      {
        path: 'customer-service/work-order-closing',
        name: 'WorkOrderClosing',
        component: () => import('@/views/WorkOrderClosing.vue'),
        meta: { title: '关闭工单' }
      },
      {
        path: 'customer-service/work-order-all',
        name: 'WorkOrderAll',
        component: () => import('@/views/WorkOrderAll.vue'),
        meta: { title: '全部工单' }
      },
      // Pending Goods Review
      {
        path: 'pending-goods-review',
        name: 'PendingGoodsReview',
        component: () => import('@/views/PendingGoodsReview.vue'),
        meta: { title: '分配客服' }
      },
      // Goods Processing
      {
        path: 'goods-processing',
        name: 'GoodsProcessing',
        component: () => import('@/views/GoodsProcessing.vue'),
        meta: { title: '初审商品' }
      },
      // Goods Audit
      {
        path: 'goods-audit',
        name: 'GoodsAudit',
        component: () => import('@/views/GoodsAudit.vue'),
        meta: { title: '复审商品' }
      },
      // VIP Package Management
      {
        path: 'vip-package-management',
        name: 'VipPackageManagement',
        component: () => import('@/views/VipPackageManagement.vue'),
        meta: { title: '会员管理' }
      },
      // Rcoin Recharge
      {
        path: 'rcoin-recharge',
        name: 'RcoinRecharge',
        component: () => import('@/views/RcoinRecharge.vue'),
        meta: { title: '研学币充值' }
      },
      // All Goods
      {
        path: 'all-goods',
        name: 'AllGoods',
        component: () => import('@/views/AllGoods.vue'),
        meta: { title: '商品列表' }
      },
      // User Management
      {
        path: 'user/list',
        name: 'UserList',
        component: () => import('@/views/UserManagement/UserList.vue'),
        meta: { title: '用户列表' }
      },
      {
        path: 'user/settings',
        name: 'UserSettings',
        component: () => import('@/views/UserManagement/UserSettings.vue'),
        meta: { title: '用户设置' }
      },
      // School Management
      {
        path: 'school/list',
        name: 'SchoolList',
        component: () => import('@/views/SchoolManagement/SchoolList.vue'),
        meta: { title: '学校列表' }
      },
      {
        path: 'school/departments',
        name: 'SchoolDepartments',
        component: () => import('@/views/SchoolManagement/SchoolDepartments.vue'),
        meta: { title: '学部院校' }
      },
      {
        path: 'school/majors',
        name: 'SchoolMajors',
        component: () => import('@/views/SchoolManagement/SchoolMajors.vue'),
        meta: { title: '院校专业' }
      },
      {
        path: 'school/enrollment-plan',
        name: 'EnrollmentPlan',
        component: () => import('@/views/SchoolManagement/EnrollmentPlan.vue'),
        meta: { title: '招生计划' }
      },
      {
        path: 'school/research-direction',
        name: 'ResearchDirection',
        component: () => import('@/views/SchoolManagement/ResearchDirection.vue'),
        meta: { title: '研究方向' }
      },
      {
        path: 'school/preliminary-exam-subject',
        name: 'PreliminaryExamSubject',
        component: () => import('@/views/SchoolManagement/PreliminaryExamSubject.vue'),
        meta: { title: '初试科目' }
      },
      {
        path: 'school/retest-assessment',
        name: 'RetestAssessment',
        component: () => import('@/views/SchoolManagement/RetestAssessment.vue'),
        meta: { title: '复试考核' }
      },
      {
        path: 'school/preliminary-exam-books',
        name: 'PreliminaryExamBooks',
        component: () => import('@/views/SchoolManagement/PreliminaryExamBooks.vue'),
        meta: { title: '初试书目' }
      },
      // Question Bank Management
      {
        path: 'question/navigation',
        name: 'QuestionBankNavigation',
        component: () => import('@/views/QuestionBankNavigation.vue'),
        meta: { title: '题库导航' }
      },
      {
        path: 'question/management-exam',
        name: 'ManagementExam',
        component: () => import('@/views/ManagementExam.vue'),
        meta: { title: '管理联考试题' }
      },
      {
        path: 'question/management-exam-document',
        name: 'ManagementExamDocument',
        component: () => import('@/views/ManagementExamDocument.vue'),
        meta: { title: '上传管理联考试卷' }
      },
      // Community Management
      {
        path: 'community/circle-collections',
        name: 'CircleCollection',
        component: () => import('@/views/Community/CircleCollection.vue'),
        meta: { title: '圈子合集' }
      },
      {
        path: 'community/posts',
        name: 'PostList',
        component: () => import('@/views/Community/PostList.vue'),
        meta: { title: '圈子列表' }
      },
      {
        path: 'community/circle-members',
        name: 'CircleMembers',
        component: () => import('@/views/Community/CircleMembers.vue'),
        meta: { title: '圈子圈友' }
      },
      {
        path: 'community/circle-posts',
        name: 'CirclePosts',
        component: () => import('@/views/Community/CirclePosts.vue'),
        meta: { title: '圈子帖子' }
      },
      {
        path: 'community/circle-posts/:circleId',
        name: 'CirclePostDetail',
        component: () => import('@/views/Community/CirclePostDetail.vue'),
        meta: { title: '圈子详情' }
      },
      {
        path: 'community/info-category',
        name: 'InfoCategory',
        component: () => import('@/views/Community/InfoCategory.vue'),
        meta: { title: '咨询分类' }
      },
      {
        path: 'community/info-list',
        name: 'InfoList',
        component: () => import('@/views/Community/InfoList.vue'),
        meta: { title: '资讯列表' }
      },
      // Transaction Management
      {
        path: 'transaction/orders',
        name: 'OrderList',
        component: () => import('@/views/Transaction/OrderList.vue'),
        meta: { title: '订单列表' }
      },
      {
        path: 'transaction/records',
        name: 'TransactionRecords',
        component: () => import('@/views/Transaction/TransactionRecords.vue'),
        meta: { title: '交易记录' }
      },
      // System Configuration
      {
        path: 'system/configuration',
        name: 'SystemConfiguration',
        component: () => import('@/views/SystemConfiguration.vue'),
        meta: { title: '系统配置' }
      },
      // System Notice
      {
        path: 'system/notice',
        name: 'SystemNotice',
        component: () => import('@/views/SystemNotice.vue'),
        meta: { title: '系统公告' }
      },
      // Customer Service
      {
        path: 'customer-service/list',
        name: 'CustomerService',
        component: () => import('@/views/CustomerService.vue'),
        meta: { title: '客服管理' }
      },
      {
        path: 'customer-service/workbench',
        name: 'CustomerServiceWorkbench',
        component: () => import('@/views/CustomerServiceWorkbench.vue'),
        meta: { title: '客服工作台' }
      }
    ]
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Navigation guard for authentication
router.beforeEach((to, _from, next) => {
  // Set page title
  if (to.meta.title) {
    document.title = `${to.meta.title} - 四零八研学社管理系统`
  } else {
    document.title = '四零八研学社管理系统'
  }

  // Check if route requires authentication
  const requiresAuth = to.path !== '/login' && to.path !== '/forgot-password'

  if (requiresAuth) {
    // Use the auth utility to check authentication status
    // This will automatically handle token expiration and redirect
    const authenticated = isAuthenticated()
    
    if (!authenticated) {
      // Not authenticated or token expired, redirect to login
      next('/login')
      return
    }
  }
  
  // Check if already authenticated and trying to access login/forgot-password
  const hasToken = getToken()
  
  if ((to.path === '/login' || to.path === '/forgot-password') && hasToken) {
    // Redirect to dashboard if already authenticated
    next('/dashboard')
  } else {
    next()
  }
})

export default router
